import { create } from 'zustand';
import type { PadOutcome } from '@/api/endpoints/qualifications';

/**
 * The call page's own state (W3, behind call_page). useCallCommands is its
 * only writer; useCallSessionView reads it next to the dialer's existing
 * sources (DialerContext, the call pool, the inbound session), which it
 * never recreates (SR3-1).
 *
 * - qualifying: the call that ended and waits for its outcome. The page
 *   keeps the prospect card on screen until it is saved (RUI-P2).
 * - countdown: the 3 s after a saved qualification, before the next series.
 * - saved: what this session qualified, for the end summary.
 */

export interface QualifyingCall {
  parallelCallId: string;
  prospectId: string;
  name: string;
  phone: string;
  company: string;
  endedAt: number;
}

export interface Countdown {
  qualificationId: string;
  parallelCallId: string;
  outcome: PadOutcome;
  /** When the next series starts (Espace adds 3 s). Ignored when it does not resume. */
  endsAt: number;
  /** Échap or an open dialog: the next series waits for « Continuer ». */
  held: boolean;
  /**
   * The server applies the qualification at its commit_at, 3 s after it
   * received it; « Annuler » is offered until just before, measured from
   * when the request left (never later than the server). Espace and Échap
   * do not move it.
   */
  undoUntil: number;
  /** Only a session the hang-up paused resumes; a call qualified outside one never dials. */
  resumes: boolean;
  /** Put back on screen if « Annuler » wins. */
  call: QualifyingCall | null;
  /** The committed qualification this one corrects, restored if « Annuler » wins. */
  previous: SavedQualification | null;
}

export interface SavedQualification {
  qualificationId: string;
  parallelCallId: string;
  revision: number;
  outcome: PadOutcome;
  name: string;
  followUpAt?: string;
}

export interface SessionSummary {
  endedAt: number;
  startedAt: number | null;
}

interface CallPageState {
  qualifying: QualifyingCall | null;
  /** Calls set aside while another one took the line (CALL-PILL-1); qualified next. */
  deferred: QualifyingCall[];
  countdown: Countdown | null;
  saved: SavedQualification[];
  summary: SessionSummary | null;
  /** The phone of a line opened for consultation (spec §4.4). */
  consultedPhone: string | null;
  /**
   * A qualification whose result is unknown (network): a re-save of the same
   * content reuses its id, so it can never land twice (DX-6).
   */
  unsent: { parallelCallId: string; qualificationId: string; key: string } | null;
  /**
   * The call whose hang-up paused a running session: only its qualification
   * resumes dialing. Never set by a pause or stop the caller chose.
   */
  resumeAfter: string | null;
  setResumeAfter(parallelCallId: string | null): void;
  /** The page replaces the list (spec §4.1): from « Lancer » until « Retour à la liste ». */
  pageOpen: boolean;
  /** « Lancer » from the list: start once the reconciliation found nothing to settle. */
  autoStart: boolean;
  open(autoStart: boolean): void;
  close(): void;
  setAutoStart(on: boolean): void;
  setQualifying(call: QualifyingCall | null): void;
  /** The next call to qualify: a deferred one, or nothing. */
  nextQualifying(): void;
  setCountdown(c: Countdown | null): void;
  addSaved(s: SavedQualification): void;
  removeSaved(qualificationId: string): void;
  setSummary(s: SessionSummary | null): void;
  setConsulted(phone: string | null): void;
  setUnsent(u: CallPageState['unsent']): void;
  reset(): void;
}

export const useCallPageStore = create<CallPageState>()((set) => ({
  qualifying: null,
  deferred: [],
  countdown: null,
  saved: [],
  summary: null,
  consultedPhone: null,
  unsent: null,
  resumeAfter: null,
  pageOpen: false,
  autoStart: false,
  open: (autoStart) => set({ pageOpen: true, autoStart, summary: null }),
  close: () => set({ pageOpen: false, autoStart: false, qualifying: null, deferred: [], countdown: null, saved: [], summary: null, consultedPhone: null, unsent: null, resumeAfter: null }),
  setAutoStart: (autoStart) => set({ autoStart }),
  // A new call to qualify never drops one still waiting: it is set aside.
  setQualifying: (qualifying) => set((st) => ({
    qualifying,
    deferred: qualifying && st.qualifying && st.qualifying.parallelCallId !== qualifying.parallelCallId
      ? [...st.deferred.filter((d) => d.parallelCallId !== st.qualifying!.parallelCallId), st.qualifying]
      : st.deferred.filter((d) => d.parallelCallId !== qualifying?.parallelCallId),
  })),
  nextQualifying: () => set((st) => ({ qualifying: st.deferred[0] ?? null, deferred: st.deferred.slice(1) })),
  setCountdown: (countdown) => set({ countdown }),
  addSaved: (s) => set((st) => ({ saved: [...st.saved.filter((x) => x.parallelCallId !== s.parallelCallId), s] })),
  removeSaved: (id) => set((st) => ({ saved: st.saved.filter((x) => x.qualificationId !== id) })),
  setSummary: (summary) => set({ summary }),
  setConsulted: (consultedPhone) => set({ consultedPhone }),
  setUnsent: (unsent) => set({ unsent }),
  setResumeAfter: (resumeAfter) => set({ resumeAfter }),
  reset: () => set({ qualifying: null, deferred: [], countdown: null, saved: [], summary: null, consultedPhone: null, unsent: null, resumeAfter: null }),
}));

/** DES-18: note drafts per call, kept across a reload until the qualification is saved. */
const DRAFT_PREFIX = 'xdial_call_note:';

export function readNoteDraft(parallelCallId: string): string {
  try { return sessionStorage.getItem(DRAFT_PREFIX + parallelCallId) ?? ''; } catch { return ''; }
}

export function writeNoteDraft(parallelCallId: string, text: string): boolean {
  try {
    if (text) sessionStorage.setItem(DRAFT_PREFIX + parallelCallId, text);
    else sessionStorage.removeItem(DRAFT_PREFIX + parallelCallId);
    return true;
  } catch {
    return false;
  }
}

export function clearNoteDraft(parallelCallId: string): void {
  try { sessionStorage.removeItem(DRAFT_PREFIX + parallelCallId); } catch { /* nothing kept */ }
}
